import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import { categories } from '../data/tools';
import { Button } from './ui/Button';
import { useLanguageRouter } from '../utils/i18nRouter';

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MobileSidebar: React.FC<MobileSidebarProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageRouter();
  const location = useLocation();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set() // 默认不展开任何分类
  );

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const getCurrentTool = () => {
    const pathParts = location.pathname.split('/');
    if (pathParts.length >= 4 && pathParts[1] === 'tools') {
      const categoryId = pathParts[2];
      const toolId = pathParts[3];
      const category = categories.find(cat => cat.id === categoryId);
      return category?.tools.find(tool => tool.id === toolId) || null;
    }
    return null;
  };

  const selectedTool = getCurrentTool();

  const colorClasses = {
    blue: 'text-blue-600 dark:text-blue-400',
    green: 'text-green-600 dark:text-green-400',
    purple: 'text-purple-600 dark:text-purple-400',
    orange: 'text-orange-600 dark:text-orange-400',
    pink: 'text-pink-600 dark:text-pink-400',
    indigo: 'text-indigo-600 dark:text-indigo-400',
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      {/* Sidebar */}
      <div className="fixed left-0 top-0 bottom-0 w-80 bg-white dark:bg-gray-800 shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <Link to={getLocalizedPath('/')} className="flex items-center space-x-2" onClick={onClose}>
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">TC</span>
            </div>
            <span className="text-lg font-bold text-gray-900 dark:text-white">
              {t('header.title')}
            </span>
          </Link>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Categories */}
        <div className="flex-1 overflow-y-auto p-4">
          
          <nav className="space-y-2">
            {categories.map((category) => {
              const IconComponent = category.icon;
              const isExpanded = expandedCategories.has(category.id);
              const colorClass = colorClasses[category.color as keyof typeof colorClasses];
              
              return (
                <div key={category.id}>
                  {/* Category Header */}
                  <Button
                    variant="ghost"
                    className="w-full justify-start p-2 h-auto text-left"
                    onClick={() => toggleCategory(category.id)}
                    aria-expanded={isExpanded}
                  >
                    <div className="flex items-center w-full">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 mr-3 ${colorClass}`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {t(category.name)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {t('home.toolsCount', { count: category.tools.length })}
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </Button>
                  
                  {/* Tools List */}
                  {isExpanded && (
                    <div className="ml-11 mt-1 space-y-1">
                      {category.tools.map((tool) => {
                        const ToolIcon = tool.icon;
                        const isSelected = selectedTool?.id === tool.id;
                        
                        return (
                          <Link
                            key={tool.id}
                            to={getLocalizedPath(tool.path)}
                            onClick={onClose}
                          >
                            <Button
                              variant={isSelected ? "secondary" : "ghost"}
                              className="w-full justify-start p-2 h-auto text-left"
                            >
                              <div className="flex items-center w-full">
                                <ToolIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-3 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm text-gray-900 dark:text-white truncate">
                                    {t(tool.name)}
                                  </div>
                                  {tool.featured && (
                                    <div className="flex items-center mt-1">
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                                        {t('home.featured')}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Button>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </div>
    </div>
  );
};
